'use client';

import React, { useState, useRef, useEffect } from 'react';

export default function MusicPlayer() {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [duration, setDuration] = useState('0:00');
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTimeUpdate = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
        setCurrentTime(formatTime(audio.currentTime));
      }
    };

    const onLoadedMetadata = () => {
      setDuration(formatTime(audio.duration));
    };

    const onEnded = () => {
      setPlaying(false);
      setProgress(0);
      setCurrentTime('0:00');
    };

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play().catch(() => {});
    }
    setPlaying(!playing);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    audio.currentTime = pct * audio.duration;
  };

  return (
    <div
      className="no-print"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: '48px',
        background: '#111111',
        borderTop: '1px solid #ff1a1a',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        gap: '12px',
      }}
    >
      {/* Track served from /public/audio */}
      <audio ref={audioRef} src="/audio/diggity-glover-final-master.m4a" preload="metadata" />

      {/* Play/Pause */}
      <button
        onClick={togglePlay}
        style={{
          background: 'none',
          border: '1px solid #ff1a1a',
          color: '#ff1a1a',
          cursor: 'pointer',
          width: '28px',
          height: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.7rem',
          flexShrink: 0,
          transition: 'box-shadow 0.2s',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 0 8px rgba(255,26,26,0.7)')}
        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {playing ? '■' : '▶'}
      </button>

      {/* Time */}
      <span
        style={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.65rem',
          color: '#666666',
          flexShrink: 0,
        }}
      >
        {currentTime} / {duration}
      </span>

      {/* Progress bar */}
      <div
        onClick={handleProgressClick}
        style={{
          flex: 1,
          height: '4px',
          background: '#333333',
          cursor: 'pointer',
          position: 'relative',
        }}
      >
        <div
          style={{
            height: '100%',
            background: '#ff1a1a',
            width: `${progress}%`,
            transition: 'width 0.1s linear',
            boxShadow: '0 0 6px rgba(255,26,26,0.7)',
          }}
        />
      </div>

      {/* Label */}
      <span
        style={{
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.6rem',
          color: '#444444',
          flexShrink: 0,
          letterSpacing: '0.08em',
        }}
      >
        WINAMP
      </span>
    </div>
  );
}
